import { memo } from "react";
import { motion } from "framer-motion";
import { ChevronDown } from "lucide-react";

import { cn } from "@/lib/utils";
import { MenuHeader, SubMenu } from "@/types/menu";

import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";

import { getMenuIcon, itemVariants } from "../constants";
import { isPathActive } from "../helpers";
import { SidebarSubMenu } from "./SidebarSubMenu";

interface SidebarItemProps {
  item: MenuHeader;
  isCollapsed: boolean;
  isExpanded: boolean;
  currentPath: string;
  currentSearch: string;
  counts: Record<string, number>;
  onToggle: (id: string) => void;
  onNavigate: (item: MenuHeader | SubMenu) => void;
}

export const SidebarItem = memo(
  ({
    item,
    isCollapsed,
    isExpanded,
    currentPath,
    currentSearch,
    counts,
    onToggle,
    onNavigate,
  }: SidebarItemProps) => {
    const Icon = getMenuIcon(item.id);

    const isActive = isPathActive(item.url, currentPath, currentSearch);
    const hasSub = !!item.subMenus && item.subMenus.length > 0;
    const isParentActive =
      hasSub && item.subMenus?.some((sub) => isPathActive(sub.url, currentPath, currentSearch));

    const isHighlighted = isActive || (isParentActive && isCollapsed);
    // Badge por rota: um item-folha usa a própria contagem; um pai soma os filhos.
    const submenuTotal = hasSub
      ? (item.subMenus ?? []).reduce((total, sub) => total + (counts[sub.id] ?? 0), 0)
      : 0;
    const badgeCount = hasSub ? submenuTotal : (counts[item.id] ?? 0);
    const showBadge = badgeCount > 0;
    // Ponto indicador do estado ativo (Direção C), só quando a direita está livre.
    const showDot = isHighlighted && !showBadge && !hasSub;

    const textColorClass = isHighlighted
      ? "text-sidebar-accent-foreground font-medium"
      : "text-sidebar-foreground/70 group-hover:text-sidebar-foreground";

    const iconColorClass =
      isActive || isParentActive
        ? "text-primary"
        : "text-sidebar-foreground/50 group-hover:text-sidebar-foreground/80";

    const activate = () => {
      if (hasSub) onToggle(item.id);
      else onNavigate(item);
    };

    const handleClick = (e: React.MouseEvent) => {
      e.preventDefault();
      activate();
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        activate();
      }
    };

    // Botão do item. No rail recolhido ele é o gatilho do HoverCard (flyout /
    // tooltip); expandido, fica solto seguido do submenu acordeão.
    const button = (
      <div
        role="button"
        tabIndex={0}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        className={cn(
          "group relative flex cursor-pointer select-none items-center overflow-hidden rounded-lg outline-none transition-colors focus-visible:ring-2 focus-visible:ring-sidebar-ring",
          isCollapsed ? "mx-2 justify-center p-2" : "mx-2 justify-between px-3 py-2.5",
          textColorClass,
        )}
      >
        {isHighlighted && (
          <motion.div
            layoutId="sidebar-active-item"
            className="absolute inset-0 rounded-lg border border-sidebar-border bg-sidebar-accent/60 shadow-sm"
            initial={false}
            transition={{ duration: 0.2, ease: "easeOut" }}
          />
        )}

        {!isHighlighted && (
          <div className="absolute inset-0 rounded-lg bg-transparent transition-colors duration-200 group-hover:bg-sidebar-accent/50" />
        )}

        <div className="relative z-10 flex min-w-0 items-center gap-3">
          <div className="relative flex items-center justify-center">
            <Icon
              className={cn(
                "h-5 w-5 transition-all duration-200 ease-out group-hover:scale-110",
                iconColorClass,
              )}
            />

            {showBadge && isCollapsed && (
              <motion.span
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.2 }}
                className="absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-destructive text-[10px] font-bold text-destructive-foreground shadow-sm ring-2 ring-sidebar"
              >
                {badgeCount > 9 ? "9+" : badgeCount}
              </motion.span>
            )}
          </div>

          {!isCollapsed && <span className="truncate text-sm font-medium">{item.nome}</span>}
        </div>

        {!isCollapsed && (showBadge || hasSub || showDot) && (
          <div className="relative z-10 flex items-center gap-2">
            {showDot && <span className="h-1.5 w-1.5 rounded-full bg-primary" />}

            {showBadge && (
              <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1.5 text-[10px] font-bold text-destructive-foreground shadow-sm">
                {badgeCount > 9 ? "9+" : badgeCount}
              </span>
            )}

            {hasSub && (
              <motion.div
                className={cn(
                  "transition-colors",
                  isActive || isParentActive
                    ? "text-primary"
                    : "text-sidebar-foreground/50 group-hover:text-sidebar-foreground",
                )}
                animate={{ rotate: isExpanded ? 180 : 0 }}
                transition={{ duration: 0.2 }}
              >
                <ChevronDown size={14} />
              </motion.div>
            )}
          </div>
        )}
      </div>
    );

    // Recolhido: o flyout/tooltip é renderizado em portal (HoverCard), então
    // não depende do overflow da nav — a nav pode rolar e o painel ainda
    // escapa para fora do rail, sempre reposicionado dentro da viewport.
    if (isCollapsed) {
      return (
        <motion.div className="relative mb-1" variants={itemVariants}>
          <HoverCard>
            <HoverCardTrigger render={button} />
            <HoverCardContent
              side="right"
              align={hasSub ? "start" : "center"}
              sideOffset={10}
              className={cn(
                "border-sidebar-border bg-sidebar p-0 text-sidebar-foreground",
                hasSub ? "w-[220px]" : "w-auto",
              )}
            >
              {hasSub ? (
                <div className="flex max-h-[70vh] flex-col">
                  <p className="shrink-0 truncate px-4 pb-1.5 pt-2.5 text-xs font-semibold text-sidebar-foreground/60">
                    {item.nome}
                  </p>
                  <div className="custom-scrollbar space-y-0.5 overflow-y-auto p-1.5 pt-0">
                    {item.subMenus!.map((sub) => {
                      const subActive = isPathActive(sub.url, currentPath, currentSearch);
                      return (
                        <a
                          key={sub.id}
                          href={sub.url}
                          onClick={(e) => {
                            e.preventDefault();
                            onNavigate(sub);
                          }}
                          className={cn(
                            "flex items-center justify-between gap-2 rounded-md px-2.5 py-2 text-sm outline-none transition-colors",
                            subActive
                              ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground"
                              : "text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground",
                          )}
                        >
                          <span className="truncate">{sub.nome}</span>
                          {(counts[sub.id] ?? 0) > 0 && (
                            <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-destructive-foreground">
                              {counts[sub.id] > 9 ? "9+" : counts[sub.id]}
                            </span>
                          )}
                        </a>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <span className="block whitespace-nowrap px-3 py-1.5 text-xs font-medium">
                  {item.nome}
                </span>
              )}
            </HoverCardContent>
          </HoverCard>
        </motion.div>
      );
    }

    return (
      <motion.div className="relative mb-1" variants={itemVariants}>
        {button}

        {hasSub && (
          <SidebarSubMenu
            subMenus={item.subMenus!}
            isOpen={isExpanded}
            currentPath={currentPath}
            currentSearch={currentSearch}
            counts={counts}
            onNavigate={onNavigate}
          />
        )}
      </motion.div>
    );
  },
);
SidebarItem.displayName = "SidebarItem";
